import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import {
    getContourUndoService,
    getRoiBaselineStore,
} from '~/domain/ServiceContainer';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useEnvironmentViews } from '~/hooks/environment/useEnvironmentViews';
import {
    DifferenceReferenceMode,
    differenceReferenceModeState,
} from '~/state/atoms/toolsAtoms';
import { attachComparisonOverlays } from './comparisonOverlay';
import {
    buildReferenceDiffRegions,
    resolveReferenceTexture,
} from './referenceDiff';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Counter that increments whenever the reference-diff overlay for `roiId`
 * must recompute:
 *  - The ROI's live contour texture changed. A normal edit publishes a
 *    `RoiContoursModifiedEvent`, but undo/redo restore the SDF through
 *    `ContourUndoRequestedEvent`/`ContourRedoRequestedEvent` WITHOUT
 *    re-emitting `RoiContoursModifiedEvent` — so all three must be observed, or
 *    the overlay would keep painting a diff of a contour that was reverted.
 *  - The async reference the overlay peeks at settled. Both same-slice references
 *    are committed ASYNCHRONOUSLY (worker-compressed) AFTER the edit event, so
 *    recomputing on the edit event alone would read the store before the commit
 *    lands — the overlay would stay empty until an unrelated re-render. Each store
 *    notifies once its commit completes: the undo service for 'pre-operation', the
 *    baseline store (populated on the ROI's first edit) for 'original-ai'. Without
 *    the 'original-ai' watch, the diff does not appear after the first edit until
 *    the mode is toggled.
 */
function useRoiEditToken(
    roiId: RoiId | null,
    active: boolean,
    mode: DifferenceReferenceMode
): number {
    const [token, setToken] = useState(0);
    useEffect(() => {
        if (!active || roiId === null) {
            return;
        }
        const bump = () => setToken((value) => value + 1);
        const bumpForRoi = (event: RoiContourEditEvent) => {
            if (event.roiId === roiId) {
                bump();
            }
        };
        const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((eventType) =>
            domainEventBus.subscribe(eventType, bumpForRoi)
        );
        // Recompute once the async reference for the active mode lands (see above).
        if (mode === 'pre-operation') {
            subscriptions.push(getContourUndoService().subscribe(bump));
        } else if (mode === 'original-ai') {
            subscriptions.push(getRoiBaselineStore().subscribe(bump));
        }
        return () =>
            subscriptions.forEach((subscription) => subscription.unsubscribe());
    }, [active, roiId, mode]);
    return token;
}

/**
 * Drives the same-slice difference overlay: highlights where the selected ROI
 * differs (green = added, red = removed) from a reference version of ITSELF —
 * its state before the last operation, or its original Contour+ prediction. The
 * "previous slice" mode is the engine cross-slice shader (kept in sync here),
 * not the same-slice overlay below. Mount once in a component that outlives
 * the grid's tiles.
 *
 * The overlay is drawn on the edit target's Environment only, on every grid
 * tile that shows that Environment (a second row of the same series shares
 * it). A view that mounts while a mode is on receives the overlay; a view
 * that unmounts has it removed, which frees the textures the view's own
 * teardown leaves behind.
 *
 * Mirrors {@link useContourComparison}: it precomputes the added/removed region
 * textures via SDF booleans and draws them per plane view through a
 * {@link ComparisonObject}, rebuilding on ROI edit.
 */
export function useReferenceDiff(): void {
    const mode = useRecoilValue(differenceReferenceModeState);
    const {
        environment,
        structureSet,
        roiId: selectedRoi,
    } = useActiveEditTarget();
    const views = useEnvironmentViews(environment);

    // Only the same-slice reference modes are this hook's business.
    const active = mode === 'pre-operation' || mode === 'original-ai';
    const editToken = useRoiEditToken(selectedRoi, active, mode);

    // Reactive visibility of the selected ROI: a visibility toggle publishes a
    // fresh read model, re-running the overlay effect so it tears the overlay
    // down for a hidden structure (see the isVisible gate below).
    const selectedRoiVisible =
        structureSet?.roiArray.find((roi) => roi.roiId === selectedRoi)
            ?.visible ?? false;

    // Keep the engine's cross-slice shader (the 'previous-slice' mode) in sync
    // with the selected mode. Owned here, in an always-mounted viewer hook, so
    // the shader is never left on after the toolbar picker unmounts. The flag
    // is per Environment and has one writer: the cleanup lifts it from the
    // Environment it was set on, so a change of mode or of edit target and an
    // unmount leave none shaded.
    useEffect(() => {
        if (!environment || mode !== 'previous-slice') {
            return;
        }
        environment.updateCrossSliceDiff(true);
        return () => environment.updateCrossSliceDiff(false);
    }, [mode, environment]);

    useEffect(() => {
        if (
            !active ||
            !environment ||
            selectedRoi === null ||
            views.length === 0
        ) {
            return;
        }
        const roi = environment.getRoi(selectedRoi);
        // A hidden or empty structure draws no overlay, matching the cross-slice
        // path (TextureView.renderLayers) — a decluttered view must not keep
        // painting a hidden structure's difference fills.
        const currentTex = roi?.drawableTexture;
        if (!roi || !currentTex) {
            return;
        }

        // No reference yet (e.g. no edits in pre-operation mode, or no captured
        // baseline): nothing to diff, so draw nothing.
        const referenceTex = resolveReferenceTexture(mode, roi);
        if (!referenceTex) {
            return;
        }

        const regions = buildReferenceDiffRegions(currentTex, referenceTex);
        // The regions own their own textures; the reference was only an input.
        referenceTex.delete();

        // Draw one overlay per view (each owning its own region-texture clones);
        // the returned teardown removes them and repaints.
        return attachComparisonOverlays(views, regions, environment);
    }, [
        active,
        mode,
        selectedRoi,
        selectedRoiVisible,
        environment,
        views,
        editToken,
    ]);
}
